import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";

import PropertyCard, { PropertyCardItem } from "components/properties/PropertyCard";
import PropertyRow from "components/properties/PropertyRow";
import { listPropertiesPaged } from "api/properties";
import { useI18n } from "i18n/I18nProvider";

type Props = {
  locale: string;
  initialSearch?: string; // <-- NEW: παίρνουμε τα φίλτρα από το URL που ήρθαν από Home
};

function mapItem(p: any): PropertyCardItem {
  return {
    id: String(p.id),
    slug: String(p.slug ?? ""),
    display_title: p.display_title ?? "",
    city: p.city ?? "",
    region: p.region ?? "",
    listing_status: p.listing_status ?? "rent",
    persons: p.persons ?? null,
    label: p.label ?? null,
    price: p.price ?? null,
    price_currency: p.price_currency ?? "EUR",
    price_on_call: !!p.price_on_call,
    bedrooms: p.bedrooms ?? null,
    bathrooms: p.bathrooms ?? null,
    size_m2: p.size_m2 ?? null,
    coverImage: p.coverImage ?? p.featured_image_url ?? null,
  };
}

function parseSearch(search: string) {
  const sp = new URLSearchParams(search || "");
  return {
    q: sp.get("q") || "",
    city: sp.get("city") || "",
    status: sp.get("status") || "",
    property_type: sp.get("property_type") || "",
    label: sp.get("label") || "",
  };
}

export default function PropertiesClient({ locale, initialSearch = "" }: Props) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  const PAGE_SIZE = 18;
  const TYPES = ["stonehouse", "house", "apartment", "maisonette", "studio"];

  // ---------------------------
  // URL -> state (source of truth at entry)
  // ---------------------------
  const initialFromUrl = React.useMemo(() => parseSearch(initialSearch || location.search), [initialSearch, location.search]);

  const [q, setQ] = React.useState(initialFromUrl.q);
  const [city, setCity] = React.useState(initialFromUrl.city);
  const [status, setStatus] = React.useState(initialFromUrl.status);
  const [propertyType, setPropertyType] = React.useState(initialFromUrl.property_type);
  const [label, setLabel] = React.useState(initialFromUrl.label);

  // View state
  const [view, setView] = React.useState<"grid" | "list">("grid");

  // Paging state
  const [rows, setRows] = React.useState<any[]>([]);
  const [offset, setOffset] = React.useState<number>(0);
  const [hasMore, setHasMore] = React.useState<boolean>(false);
  const [total, setTotal] = React.useState<number>(0);

  const [loadingMore, setLoadingMore] = React.useState(false);
  const [loadingFirst, setLoadingFirst] = React.useState(false);

  const items = React.useMemo(() => (rows || []).map(mapItem), [rows]);

  const [typeOpen, setTypeOpen] = React.useState(false);

  // Debounce + cancel stale
  const debounceRef = React.useRef<number | null>(null);
  const reqIdRef = React.useRef(0);

  // Build query string from current filters
  const buildQueryString = React.useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (city) params.set("city", city);
    if (status) params.set("status", status);
    if (propertyType) params.set("property_type", propertyType);
    if (label) params.set("label", label);
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, [q, city, status, propertyType, label]);

  // First page fetch
  const fetchFirstPage = React.useCallback(async () => {
    const myReqId = ++reqIdRef.current;

    setLoadingFirst(true);
    try {
      const res = await listPropertiesPaged({
        q: q || undefined,
        city: city || undefined,
        status: status === "rent" || status === "sale" ? status : undefined,
        property_type: propertyType || undefined,
        label: label || undefined,
        limit: PAGE_SIZE,
        offset: 0,
      });

      if (myReqId !== reqIdRef.current) return;

      const newItems = res.items || [];
      setRows(newItems);
      setOffset(newItems.length);
      setHasMore(!!res.hasMore);
      setTotal(res.total ?? 0);
    } finally {
      if (myReqId === reqIdRef.current) setLoadingFirst(false);
    }
  }, [q, city, status, propertyType, label]);

  // ---------------------------
  // When URL changes by navigation from elsewhere (Home chips etc)
  // -> update state + fetch
  // ---------------------------
  React.useEffect(() => {
    const u = parseSearch(location.search);

    // Αν τα URL params άλλαξαν (π.χ. ήρθες από home),
    // κάνε sync στα state
    setQ(u.q);
    setCity(u.city);
    setStatus(u.status);
    setPropertyType(u.property_type);
    setLabel(u.label);
    // και μετά fetch (debounced)
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => fetchFirstPage(), 0);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ---------------------------
  // State filters changed by user -> sync URL + debounced fetch
  // ---------------------------
  React.useEffect(() => {
    const qs = buildQueryString();
    const target = `/${locale}/properties${qs}`;

    // sync URL only if differs
    const current = `${location.pathname}${location.search}`;
    if (target !== current) {
      navigate(target, { replace: true });
    }

    // debounce fetch
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      fetchFirstPage();
    }, 350);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [q, city, status, propertyType, label, locale, buildQueryString, fetchFirstPage, navigate, location.pathname, location.search]);

  // ---------------------------
  // Load more
  // ---------------------------
  const loadMore = async () => {
    if (!hasMore || loadingMore || loadingFirst) return;

    const myReqId = ++reqIdRef.current;

    setLoadingMore(true);
    try {
      const res = await listPropertiesPaged({
        q: q || undefined,
        city: city || undefined,
        status: status === "rent" || status === "sale" ? status : undefined,
        property_type: propertyType || undefined,
        label: label || undefined,
        limit: PAGE_SIZE,
        offset,
      });

      if (myReqId !== reqIdRef.current) return;

      const nextItems = res.items || [];
      setRows((prev) => [...prev, ...nextItems]);
      setOffset((prev) => prev + nextItems.length);
      setHasMore(!!res.hasMore);
      setTotal(res.total ?? total);
    } finally {
      if (myReqId === reqIdRef.current) setLoadingMore(false);
    }
  };

  const clearFilters = () => {
    setQ("");
    setCity("");
    setStatus("");
    setPropertyType("");
    setLabel("");
  };

  return (
    <Box sx={{ py: { xs: 4, md: 7 } }}>
      <Container maxWidth="lg">
        <Stack spacing={1}>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: -0.5 }}>
            {t("properties.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("properties.subtitle")}
          </Typography>
        </Stack>

        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Stack spacing={2}>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr auto" },
                alignItems: "center",
              }}
            >
              <TextField
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("properties.searchPlaceholder")}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={t("properties.cityPlaceholder")}
                fullWidth
              />

              <ToggleButtonGroup
                value={status || ""}
                exclusive
                onChange={(_, v) => setStatus(v ?? "")}
                size="small"
                sx={{
                  height: 56,
                  "& .MuiToggleButton-root": { borderRadius: 999, px: 1.5, fontWeight: 800 },
                }}
              >
                <ToggleButton value="">{t("properties.statusAny")}</ToggleButton>
                <ToggleButton value="rent">{t("properties.statusRent")}</ToggleButton>
                <ToggleButton value="sale">{t("properties.statusSale")}</ToggleButton>
              </ToggleButtonGroup>

              <Button
                variant="outlined"
                onClick={() => setTypeOpen(true)}
                sx={{ borderRadius: 999, whiteSpace: "nowrap", height: 56, fontWeight: 900 }}
              >
                {propertyType
                  ? t("properties.typeSelected", { type: t(`propertyTypes.${propertyType}`) })
                  : t("properties.selectType")}
              </Button>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <ToggleButtonGroup
                value={view}
                exclusive
                onChange={(_, v) => v && setView(v)}
                size="small"
                sx={{ "& .MuiToggleButton-root": { borderRadius: 2 } }}
              >
                <ToggleButton value="grid" aria-label="grid">
                  <ViewModuleIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="list" aria-label="list">
                  <ViewListIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>

              <Button variant="text" onClick={clearFilters} sx={{ fontWeight: 900 }}>
                {t("properties.clear")}
              </Button>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {q ? <Chip label={t("properties.chipQ", { q })} onDelete={() => setQ("")} /> : null}
            {city ? <Chip label={t("properties.chipCity", { city })} onDelete={() => setCity("")} /> : null}
            {status ? (
              <Chip
                label={t("properties.chipStatus", { status: t(`properties.status.${status}`) })}
                onDelete={() => setStatus("")}
              />
            ) : null}
            {propertyType ? (
              <Chip
                label={t("properties.chipType", { type: t(`propertyTypes.${propertyType}`) })}
                onDelete={() => setPropertyType("")}
              />
            ) : null}
            {label ? <Chip label={label} onDelete={() => setLabel("")} /> : null}

            <Chip label={t("properties.resultsCount", { shown: items.length, total })} />
          </Stack>
        </Box>

        {/* Loading first page */}
        {loadingFirst && items.length === 0 ? (
          <Typography sx={{ mt: 3 }} color="text.secondary">
            {t("properties.loadingFirst") || "Loading..."}
          </Typography>
        ) : null}

        {view === "grid" ? (
          <Box
            sx={{
              mt: 3,
              display: "grid",
              gap: 16,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
            }}
          >
            {items.map((p) => (
              <PropertyCard key={p.id} locale={locale} property={p} />
            ))}
          </Box>
        ) : (
          <Stack spacing={2} sx={{ mt: 3 }}>
            {items.map((p) => (
              <PropertyRow key={p.id} locale={locale} property={p} />
            ))}
          </Stack>
        )}

        <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
          {hasMore ? (
            <Button
              variant="outlined"
              onClick={loadMore}
              disabled={loadingMore || loadingFirst}
              sx={{ borderRadius: 999, px: 4, py: 1.2, fontWeight: 900 }}
            >
              {loadingMore ? t("properties.loadingMore") : t("properties.loadMore")}
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("properties.end")}
            </Typography>
          )}
        </Box>

        <Dialog open={typeOpen} onClose={() => setTypeOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle sx={{ fontWeight: 900 }}>{t("properties.typeDialogTitle")}</DialogTitle>
          <DialogContent dividers>
            <List disablePadding>
              <ListItemButton
                selected={!propertyType}
                onClick={() => {
                  setPropertyType("");
                  setTypeOpen(false);
                }}
                sx={{ borderRadius: 2, mb: 0.5 }}
              >
                <ListItemText primary={t("properties.typeAll")} />
              </ListItemButton>

              {TYPES.map((tKey) => (
                <ListItemButton
                  key={tKey}
                  selected={propertyType === tKey}
                  onClick={() => {
                    setPropertyType(tKey);
                    setTypeOpen(false);
                  }}
                  sx={{ borderRadius: 2, mb: 0.5 }}
                >
                  <ListItemText primary={t(`propertyTypes.${tKey}`)} />
                </ListItemButton>
              ))}
            </List>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setTypeOpen(false)} sx={{ fontWeight: 900 }}>
              {t("common.close")}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
